///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba, type color } from "@stovokor/math/color"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { StormTrackPoint } from "./HurricanesTable"
import { densifiedTrackPositions } from "./stormTrack"

const cObservedWidth = 1.5
const cForecastWidth = 2.5
const cFocusedWidthBoost = 1

// Observed history is dimmed so the brighter forecast reads as "what's next"
const cObservedAlphaScale = 0.5

/** One rendered run of consecutive same-intensity track segments */
interface TrackRun {
	line: PolylineNode
	baseColor: color
	baseWidth: number
}

/**
 * Observed + forecast track for one storm, draped just above the surface and
 * tinted with the Saffir-Simpson category ramp so intensity along the path is
 * readable at a glance (dim history, bright forecast). Consecutive track
 * segments sharing a category render as one geodesically densified polyline;
 * each segment takes the color of its later point so the line always shows
 * where the storm is heading in intensity terms.
 */
export class StormTrackNode extends NodeBase {
	private readonly runs: TrackRun[] = []
	private focused = false

	constructor(
		context: StovokorContext,
		observed: StormTrackPoint[],
		forecast: StormTrackPoint[],
		altitudeKm: number
	) {
		super()

		this.buildRuns(context, observed, altitudeKm, true)

		// Connect the forecast to the end of the observed track so the two
		// segments read as one continuous path
		const lastObserved = observed[observed.length - 1]
		const forecastPoints = lastObserved ? [lastObserved, ...forecast] : forecast
		this.buildRuns(context, forecastPoints, altitudeKm, false)
	}

	/** Split a track into runs of equal category color and add a line per run */
	private buildRuns(
		context: StovokorContext,
		points: StormTrackPoint[],
		altitudeKm: number,
		observed: boolean
	): void {
		if (points.length < 2) return

		let runStart = 0
		let runColor = segmentColor(points[1], observed)

		const emit = (endIndex: number) => {
			const runPoints = points.slice(runStart, endIndex + 1)
			const positions = densifiedTrackPositions(runPoints, altitudeKm)
			if (positions.length < 2) return
			const width = observed ? cObservedWidth : cForecastWidth
			const line = new PolylineNode(context, {
				positions,
				width,
				color: runColor,
				depthWrite: false,
				polygonOffset: cSurfaceOverlayDepthBias,
			})
			this.runs.push({ line, baseColor: runColor, baseWidth: width })
			this.addNode(line)
		}

		for (let i = 2; i < points.length; i++) {
			const nextColor = segmentColor(points[i], observed)
			if (!sameColor(nextColor, runColor)) {
				emit(i - 1)
				runStart = i - 1
				runColor = nextColor
			}
		}
		emit(points.length - 1)
	}

	/** Brighten and thicken the track while the storm is selected */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) return
		this.focused = focused

		const boost = focused ? cFocusedWidthBoost : 0
		const alphaFactor = focused ? 1.3 : 1
		for (const run of this.runs) {
			run.line.updateWidth(run.baseWidth + boost)
			run.line.updateColor(withAlphaBoost(run.baseColor, alphaFactor))
		}
	}
}

/** Category ramp color for the segment ending at this point */
function segmentColor(point: StormTrackPoint, observed: boolean): color {
	const base = DesignColors.stormCategoryColor(point.category, point.wind_kt)
	return observed ? rgba(base.r, base.g, base.b, base.a * cObservedAlphaScale) : base
}

function sameColor(a: color, b: color): boolean {
	return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a
}

function withAlphaBoost(base: color, factor: number): color {
	return rgba(base.r, base.g, base.b, Math.min(1, base.a * factor))
}
